import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSLATIONS = ['KJV', 'NIV', 'ESV', 'NASB', 'RVR1909', 'WEB', 'ASV', 'YLT'];
const LEANS = [
  { id: 'reformed', en: 'Reformed', es: 'Reformada' },
  { id: 'wesleyan', en: 'Wesleyan', es: 'Wesleyana' },
  { id: 'catholic', en: 'Catholic', es: 'Católica' },
  { id: 'pentecostal', en: 'Pentecostal', es: 'Pentecostal' },
  { id: 'anglican', en: 'Anglican', es: 'Anglicana' },
  { id: 'baptist', en: 'Baptist', es: 'Bautista' },
  { id: 'lutheran', en: 'Lutheran', es: 'Luterana' },
  { id: 'non-denominational', en: 'Non-denominational', es: 'No denominacional' },
];

const CompanionSettingsDialog = ({ open, onOpenChange, language }) => {
  const isES = language === 'es';
  const [translation, setTranslation] = useState('KJV');
  const [lean, setLean] = useState('non-denominational');
  const [showReasoning, setShowReasoning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${BACKEND_URL}/api/lumera/preferences`, { withCredentials: true })
      .then((r) => {
        setTranslation(r.data?.preferred_translation || 'KJV');
        setLean(r.data?.doctrinal_lean || 'non-denominational');
        setShowReasoning(!!r.data?.show_reasoning);
      })
      .catch(() => {});
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/lumera/preferences`,
        {
          preferred_translation: translation,
          doctrinal_lean: lean,
          show_reasoning: showReasoning,
        },
        { withCredentials: true },
      );
      onOpenChange(false);
    } catch (e) {
      console.error('save prefs failed', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="companion-settings-dialog">
        <DialogHeader>
          <DialogTitle>{isES ? 'Personalizar Compañero' : 'Personalize Companion'}</DialogTitle>
          <DialogDescription>
            {isES
              ? 'Ajusta tus preferencias para que las respuestas se adapten a tu tradición.'
              : 'Tune your preferences so responses match your tradition and study depth.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="prefs-translation" className="text-sm font-semibold">
              {isES ? 'Traducción preferida' : 'Preferred translation'}
            </Label>
            <Select value={translation} onValueChange={setTranslation}>
              <SelectTrigger id="prefs-translation" data-testid="prefs-translation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSLATIONS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prefs-lean" className="text-sm font-semibold">
              {isES ? 'Tradición doctrinal' : 'Doctrinal lean'}
            </Label>
            <Select value={lean} onValueChange={setLean}>
              <SelectTrigger id="prefs-lean" data-testid="prefs-lean">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEANS.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{isES ? l.es : l.en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border p-3 bg-indigo-50/40 dark:bg-indigo-950/20">
            <div className="space-y-0.5">
              <Label htmlFor="prefs-reasoning" className="text-sm font-semibold">
                {isES ? 'Mostrar razonamiento' : 'Show my reasoning'}
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {isES
                  ? 'El Compañero incluirá 3-5 pasos transparentes (contexto → paralelos → comentaristas → aplicación).'
                  : 'Companion will include 3-5 transparent steps (context → parallels → commentators → application).'}
              </p>
            </div>
            <Switch
              id="prefs-reasoning"
              checked={showReasoning}
              onCheckedChange={setShowReasoning}
              data-testid="prefs-show-reasoning"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {isES ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="prefs-save-btn"
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? (isES ? 'Guardando…' : 'Saving…') : (isES ? 'Guardar' : 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompanionSettingsDialog;
